/**
 * CaregiverPhoneMock — recreated mobile UI of the Afyanex shift-report
 * screen (the actual screen caregivers / aides / PSWs see in the field).
 *
 * Built as inline JSX (no screenshot dependency) so it scales crisply at
 * any DPR and stays aligned with brand. Mirrors:
 *   - top app bar with avatar + breadcrumb
 *   - client header + clock-in button
 *   - "From last shift" AI handoff briefing
 *   - 3 voice action cards (AI Voice Fill / Voice update to family / Voice handoff)
 *   - Today's medications row (Lisinopril)
 */
import { Mic, Sparkles, Pill, Activity, Users, Headphones, ArrowLeft, MapPin } from "lucide-react";

export default function CaregiverPhoneMock() {
  return (
    <div className="relative mx-auto w-full max-w-[300px]" data-testid="landing-caregiver-mock">
      {/* Phone frame */}
      <div className="relative rounded-[36px] bg-[#0B1530] p-2.5 shadow-[0_40px_100px_-30px_rgba(0,94,255,0.45)] ring-1 ring-clinical-border/40">
        {/* Notch */}
        <div className="absolute left-1/2 top-2 -translate-x-1/2 z-10 h-5 w-24 rounded-b-2xl bg-[#0B1530]" />
        {/* Screen */}
        <div className="relative rounded-[28px] overflow-hidden bg-clinical-bg h-[560px]">
          {/* Status bar */}
          <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-semibold text-clinical-text">
            <span>9:42</span>
            <span className="flex items-center gap-1">
              <span>•••</span>
              <span>5G</span>
              <span className="inline-block h-2 w-3 rounded-sm border border-clinical-text/70" />
            </span>
          </div>

          {/* App top bar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-clinical-border/60 bg-white">
            <button type="button" className="text-clinical-subtext">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="afyanex-wordmark font-heading font-bold text-[12px] tracking-tight">
              Afya<span className="nex">nex</span>
            </span>
            <span className="text-[10px] font-medium rounded-full bg-afyanex-blue/10 text-afyanex-blue px-2 py-0.5">
              S
            </span>
          </div>

          {/* Scroll area */}
          <div className="px-3.5 py-3 space-y-3 overflow-y-auto h-[calc(100%-72px)]">
            {/* Client header */}
            <div>
              <div className="text-[9px] uppercase tracking-[0.16em] text-clinical-subtext font-semibold">Shift report</div>
              <div className="font-heading font-semibold text-clinical-text text-[15px] mt-0.5">Margaret Wilson</div>
              <div className="text-[10px] text-clinical-subtext">Hypertension, mild dementia</div>
            </div>

            {/* Clock-in button */}
            <button
              type="button"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-afyanex-blue text-white text-[12px] font-semibold py-2.5 shadow-[0_8px_20px_-8px_rgba(0,94,255,0.5)]"
            >
              <MapPin className="h-3.5 w-3.5" /> Clock in here
            </button>

            {/* Handoff briefing */}
            <div className="rounded-xl bg-afyanex-pink/8 border border-afyanex-pink/20 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="h-3 w-3 text-afyanex-pink" />
                <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-afyanex-pink">From last shift</span>
              </div>
              <p className="text-[11px] text-clinical-text leading-relaxed">
                Calm morning. No acute concerns. Continue routine monitoring per
                care plan.
              </p>
            </div>

            {/* Voice fill (primary action) */}
            <div className="rounded-xl bg-gradient-to-br from-afyanex-blue/8 to-purple-100/40 border border-afyanex-blue/15 p-3 flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-afyanex-blue text-white shrink-0 shadow-[0_4px_14px_-4px_rgba(0,94,255,0.6)]">
                <Mic className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-clinical-text">AI Voice Fill</div>
                <div className="text-[10.5px] text-clinical-subtext leading-snug">
                  Talk through your shift — AI fills vitals, mood, fluid, activity & notes.
                </div>
              </div>
            </div>

            {/* Family update card */}
            <div className="rounded-xl bg-white border border-clinical-border/70 p-3 flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-afyanex-pink/12 text-afyanex-pink shrink-0">
                <Users className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-clinical-text">Voice update to family</div>
                <div className="text-[10.5px] text-clinical-subtext leading-snug">
                  AI writes a warm message for the family portal.
                </div>
              </div>
            </div>

            {/* Handoff brief */}
            <div className="rounded-xl bg-white border border-clinical-border/70 p-3 flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-700 shrink-0">
                <Headphones className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-clinical-text">Voice handoff</div>
                <div className="text-[10.5px] text-clinical-subtext leading-snug">
                  30s brief for the next caregiver — key points & to-dos.
                </div>
              </div>
            </div>

            {/* Today's medications */}
            <div className="rounded-xl bg-white border border-clinical-border/70 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-clinical-subtext">Today's medications</div>
                <span className="text-[9px] text-afyanex-blue font-medium">Tap to log</span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-afyanex-blue/8 text-afyanex-blue">
                  <Pill className="h-3.5 w-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11.5px] font-medium text-clinical-text">Lisinopril</span>
                    <span className="text-[10px] text-clinical-subtext">10 mg</span>
                  </div>
                  <div className="text-[10px] text-clinical-subtext">08:00 · Take with water</div>
                </div>
                <button type="button" className="text-[10px] rounded-md bg-emerald-100 text-emerald-700 px-2 py-1 font-medium">
                  Given
                </button>
              </div>
            </div>

            {/* Vitals row */}
            <div className="rounded-xl bg-white border border-clinical-border/70 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Activity className="h-3 w-3 text-clinical-subtext" />
                <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-clinical-subtext">Vitals · 14d</span>
              </div>
              <div className="flex items-center gap-3">
                {[
                  { label: "BP", val: "128/80" },
                  { label: "HR", val: "72" },
                  { label: "SpO₂", val: "98%" },
                ].map((v) => (
                  <div key={v.label}>
                    <div className="text-[9px] text-clinical-subtext">{v.label}</div>
                    <div className="text-[12px] font-semibold text-clinical-text font-mono">{v.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating callout */}
      <div className="hidden md:flex absolute -right-6 top-8 items-center gap-2 rounded-xl bg-white border border-clinical-border/70 px-3 py-2 shadow-[0_18px_40px_-15px_rgba(11,21,48,0.18)]">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-afyanex-blue/10 text-afyanex-blue">
          <Mic className="h-3.5 w-3.5" />
        </span>
        <div className="text-[11px]">
          <div className="font-semibold text-clinical-text">2-min shift note</div>
          <div className="text-clinical-subtext">vs 45 min typing</div>
        </div>
      </div>
    </div>
  );
}
